import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreens } from 'components/Navigation/types';
import { useAlert } from 'hooks/useAlert';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { LayoutChangeEvent } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, OPEN_BARCODE_SCANNER } from 'store/actions';
import {
  doAseDownloadElementPdfsZip,
  doAseFieldInstanceSearchBarcodeForGroup,
  doAseFieldInstanceSearchPin,
  doAseLoadPlans,
  doAseSetGroupFilter,
} from 'store/assessments/actions';
import {
  selectAseTplFieldGroup,
  selectAseFieldGroupStats,
  selectAseFieldInstancePinsForGroup,
  selectAseIsEditable,
  selectAseFieldInstanceQrsForGroups,
  selectAseTplFieldToGroup,
  selectAseIsOnline,
  selectAseFieldGroupFilter,
  selectAseExplorerOptions,
  selectAseIsApproved,
} from 'store/assessments/selectors';
import { InstanceParentParams } from 'store/assessments/types';
import { Menu } from 'components/Menu';
import { CountChips } from '../CountChips';
import { PageToolbar } from 'components/PageToolbar';
import { ButtonGroup } from 'components/ButtonGroup';

type Props = { groupId: string; parent: InstanceParentParams };

export const RepeatableListFilter: React.FC<Props> = ({ groupId, parent }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const filterKey = 'added';
  const groupFilterKey = `${filterKey}-${groupId}`;
  const groupFilter = useSelector((state) =>
    selectAseFieldGroupFilter(state, groupFilterKey),
  );
  const explorerOptions = useSelector(selectAseExplorerOptions);
  const searchValue = groupFilter.search;

  const isEditable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const isApproved = useSelector(selectAseIsApproved);

  const group = useSelector((state) =>
    selectAseTplFieldGroup(state, groupId),
  ) || {
    name: '',
    allowPdfDownload: false,
    calculatedInfo: {
      singularName: '',
      qrFieldId: undefined,
      pinFieldId: undefined,
    },
  };
  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parent?.fieldId || '-1'),
  );

  const groupStats = useSelector((state) =>
    selectAseFieldGroupStats(state, {
      groupId,
      parent,
      inAssessmentOnly: false,
    }),
  );

  const groupPins = useSelector((state) =>
    selectAseFieldInstancePinsForGroup(state, {
      groupId,
      parent: !isEditable || isOnline ? parent : null,
      inAssessmentOnly: !isEditable || isOnline,
    }),
  ).map((pin) => {
    if (parentGroup && groupStats.assessmentInstanceIds.includes(pin.id)) {
      return {
        ...pin,
        group: `In ${parentGroup.calculatedInfo.singularName}`,
        color: theme.colors.purple,
      };
    }
    return pin;
  });

  const groupQRs = useSelector((state) =>
    selectAseFieldInstanceQrsForGroups(state, {
      groupId,
      parent: !isEditable || isOnline ? parent : null,
      inAssessmentOnly: !isEditable || isOnline,
    }),
  );

  const handleSearch = React.useCallback(
    (search: string) => {
      dispatch(
        doAseSetGroupFilter({
          groupId: groupFilterKey,
          data: { ...groupFilter, search },
        }),
      );
    },
    [dispatch, groupFilter, groupFilterKey],
  );

  const handleFindByQrPress = () =>
    dispatch(
      OPEN_BARCODE_SCANNER({
        text:
          isEditable && !isOnline
            ? `scan to find an existing ${group.calculatedInfo.singularName.toLowerCase()} or to create a new one`
            : `scan to find an existing ${group.calculatedInfo.singularName.toLowerCase()}`,
        actionAfter: doAseFieldInstanceSearchBarcodeForGroup({
          groupId,
          data: '',
          parent,
        }),
      }),
    );

  const handleFindByPlanPress = () =>
    !groupPins.length
      ? alert({
          text: `No ${group.name.toLowerCase()} have pins yet. You'll be able to find them by their pins once there are`,
        })
      : dispatch(
          doAseLoadPlans({
            findNoun: group.calculatedInfo.singularName,
            pins: groupPins,
            actionAfterFind: doAseFieldInstanceSearchPin({
              groupId,
              id: '',
              parent,
            }),
          }),
        );

  const handleDownloadElementZip = () =>
    isApproved
      ? dispatch(
          doAseDownloadElementPdfsZip({
            groupId,
            parent,
          }),
        )
      : alert({
          text: 'A ZIP of all element PDFs can only be downloaded once this assessment has been approved',
        });

  const { qrFieldId, pinFieldId } = group.calculatedInfo;

  const showQRButton =
    !!qrFieldId && ((isEditable && !isOnline) || !!groupQRs.length);
  const showPinButton =
    !!pinFieldId && ((isEditable && !isOnline) || !!groupPins.length);

  const showCountChips =
    !!groupStats.questionsCount ||
    !!groupStats.checklistsCount ||
    !!groupStats.actionsCount ||
    !!groupStats.controlsCount;

  const hasEnabledFilterOptions =
    !explorerOptions.findingsNewReviewed ||
    !explorerOptions.questionsNegative ||
    !explorerOptions.questionsPositive ||
    (isEditable &&
      (!explorerOptions.questionsUnanswered ||
        !explorerOptions.findingsUnreviewed)) ||
    !!Object.keys(groupFilter.checks).length ||
    !!groupFilter.availabilityCategories?.length ||
    groupFilter.requiredFields;

  const iconButtonCount =
    (showCountChips ? 2 : 0) +
    (showQRButton ? 1 : 0) +
    (showPinButton ? 1 : 0) +
    (group.allowPdfDownload ? 1 : 0);

  const [searchFieldAbove, setSearchFieldAbove] = React.useState(false);
  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      setSearchFieldAbove(
        event.nativeEvent.layout.width < 260 + 44 * iconButtonCount,
      );
    },
    [iconButtonCount],
  );

  return (
    <PageToolbar
      onLayout={handleLayout}
      onSearch={handleSearch}
      searchPlaceholder={`Search ${group.name}`}
      searchValue={searchValue}
      searchFieldAbove={searchFieldAbove}
      leftChildren={
        showCountChips && (
          <CountChips
            questions={groupStats.questionsCount}
            questionsCompleted={groupStats.completedQuestionsCount}
            checklists={groupStats.checklistsCount}
            checklistsCompleted={groupStats.completedChecklistsCount}
            actions={groupStats.actionsCount}
            controls={groupStats.controlsCount}
            unreviewedActions={0}
            unreviewedControls={0}
            style={searchFieldAbove ? { flex: 1 } : undefined}
          />
        )
      }
    >
      {(!!showQRButton || !!showPinButton) && (
        <ButtonGroup>
          {!!showQRButton && (
            <IconButton
              icon={ICONS.QRCodeLight}
              onPress={handleFindByQrPress}
              accessibilityLabel="Find by QR"
            />
          )}
          {!!showPinButton && (
            <IconButton
              icon={ICONS.LocationPinLight}
              onPress={handleFindByPlanPress}
              accessibilityLabel="Find on Plan"
            />
          )}
        </ButtonGroup>
      )}
      <ButtonGroup>
        {group.allowPdfDownload && (
          <Menu
            iconButtonConfig={{
              icon: ICONS.ArrowDownLight,
            }}
            options={[
              {
                text: 'Download Element PDFs',
                subText: 'Get a ZIP of all PDFs',
                onPress: handleDownloadElementZip,
                icon: ICONS.ArrowDownLight,
              },
            ]}
          />
        )}
        <IconButton
          icon={ICONS.SlidersUpLight}
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.AseFieldInstancesOptions,
                params: { groupId, listType: 'added' },
              }),
            )
          }
          showBadge={hasEnabledFilterOptions}
        />
      </ButtonGroup>
    </PageToolbar>
  );
};
